import { useMutation, UseMutationOptions } from "@tanstack/react-query";

import { MessageType } from "@/components/Playground/types";
import { Content } from "@/types";
import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface ReplayChatMessagePayload {
  role: MessageType;
  content: Content[];
  tool_call_id?: string;
  name?: string;
}

export interface ReplayPlaygroundSessionChatMessageRequestI {
  variable_set_index: number;
  stream?: boolean;
  /** Optional override for the message content. When the user edits a
   *  message and then replays, we send the edited content here so the
   *  replay always uses the latest version – even if the preceding
   *  PATCH (edit) request hasn't been committed yet.
   *  May be a list when replaying one sibling from a parallel tool-response turn. */
  message?: ReplayChatMessagePayload | ReplayChatMessagePayload[];
  mock_tool_responses?: boolean;
  mock_scenario?: string | null;
  mock_stop_tools?: string | null;
}

export interface ReplayPlaygroundSessionChatMessageResponseI {
  success: boolean;
  individual_run_request_id: number;
  user_message: ReplayChatMessagePayload | ReplayChatMessagePayload[];
  status: "queued";
  deleted_count: number;
}

export interface ReplayPlaygroundSessionChatMessageParamsI {
  workspaceId: number;
  sessionId: string;
  messageId: string;
  request: ReplayPlaygroundSessionChatMessageRequestI;
}

export const useAPIReplayPlaygroundSessionChatMessage = (
  userToken: string,
  options?: UseMutationOptions<
    ReplayPlaygroundSessionChatMessageResponseI,
    Error,
    ReplayPlaygroundSessionChatMessageParamsI
  >,
) =>
  useMutation<
    ReplayPlaygroundSessionChatMessageResponseI,
    Error,
    ReplayPlaygroundSessionChatMessageParamsI
  >(
    variables =>
      fetch(
        ENDPOINTS.playground_session_chat_message_replay(
          variables.workspaceId,
          variables.sessionId,
          variables.messageId,
        ),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify(variables.request),
        },
      ).then(handleResponse),
    options,
  );
